import React, { useEffect, useState } from "react";
import supabase from "../../utils/supabase";
import { addGrade } from "../../utils/lti";
import { peSeasonLabel } from "../../data/peSeasons";
import { peCurriculum } from "../../data/peCurriculum";

interface Chapter {
  id: number;
  title: string;
  summary: string | null;
  season_id: number;
  chapter_number?: number;
}

interface Season {
  season_number: number;
  title: string;
}

interface Episode {
  vimeo_id: string | null;
  duration_seconds: number | null;
}

interface Props {
  chapterId: string;
  tier: string;
  lang: string;
  ltik: string | null;
}

// Run E2 J3 r2: Spanish labels when the viewer opens in Spanish (lang=es).
const VIEWER_T = {
  en: {
    tier: (n: string) => `Tier ${n}`,
    loadFailed: "Failed to load chapter",
    notFound: "Content not found",
    noVideo: "Video not yet available for this tier/language.",
    markComplete: "Mark Complete",
    completed: "✓ Completed",
    saving: "Saving…",
    noToken: "LTI token not found — cannot record completion.",
  },
  es: {
    tier: (n: string) => `Nivel ${n}`,
    loadFailed: "No se pudo cargar el capítulo",
    notFound: "No se encontró el contenido",
    noVideo: "El video todavía no está disponible para este nivel e idioma.",
    markComplete: "Marcar como completado",
    completed: "✓ Completado",
    saving: "Guardando…",
    noToken: "No se encontró el token de LTI. No se registró la finalización.",
  },
};

const LANG_LABELS: Record<string, string> = {
  en: "English",
  es: "Español",
};

const PeChapterViewer: React.FC<Props> = ({ chapterId, tier, lang, ltik }) => {
  const vt = VIEWER_T[lang === "es" ? "es" : "en"];
  const [chapter, setChapter] = useState<Chapter | null>(null);
  const [season, setSeason] = useState<Season | null>(null);
  const [episode, setEpisode] = useState<Episode | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [chapterRes, episodeRes] = await Promise.all([
          supabase
            .from("pe_chapters")
            .select("id, title, summary, season_id, chapter_number")
            .eq("id", chapterId)
            .maybeSingle(),
          supabase
            .from("pe_episodes")
            .select("vimeo_id, duration_seconds")
            .eq("chapter_id", chapterId)
            .eq("tier", tier)
            .eq("language", lang)
            .maybeSingle(),
        ]);

        if (chapterRes.error) throw new Error(vt.loadFailed);
        if (!chapterRes.data) throw new Error(vt.notFound);

        setChapter(chapterRes.data);
        setEpisode(episodeRes.data ?? null);

        const { data: seasonData } = await supabase
          .from("pe_seasons")
          .select("season_number, title")
          .eq("id", chapterRes.data.season_id)
          .maybeSingle();
        if (seasonData) setSeason(seasonData);
      } catch (err) {
        setLoadError(
          err instanceof Error ? err.message : vt.loadFailed
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [chapterId, tier, lang]);

  const handleMarkComplete = async () => {
    const token = ltik ?? localStorage.getItem("ltiToken");
    if (!token) {
      setCompleteError(vt.noToken);
      return;
    }
    setCompleting(true);
    setCompleteError(null);
    const result = await addGrade(token, 1, "completed", "percentage");
    setCompleting(false);
    if (result?.error) {
      setCompleteError(String(result.error));
    } else {
      setCompleted(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (loadError || !chapter) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-6">
          <p className="text-red-600 font-medium">{loadError ?? vt.notFound}</p>
        </div>
      </div>
    );
  }

  // Spanish chapter name and summary come from peCurriculum.ts (pe_chapters is English only).
  const curr = season
    ? peCurriculum.find((c) => c.season === season.season_number && c.chapter === chapter.chapter_number)
    : undefined;
  const summary = (lang === "es" && curr?.summary_es) || chapter.summary;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {season && (
          <p className="text-sm text-indigo-600 font-semibold mb-1 uppercase tracking-wide">
            {/* Run E2 J3: canon season name by number (pe_seasons.title can lag the SQL fix). */}
            {peSeasonLabel(season.season_number, lang === "es" ? "es" : "en")}
          </p>
        )}

        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          {/* Run E2 J3: approved Spanish chapter name (pe_chapters has English only). */}
          {(lang === "es" && curr?.title_es) || chapter.title}
        </h1>

        <div className="flex gap-2 mb-5">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            {/^T[1-4]$/.test(tier) ? vt.tier(tier.slice(1)) : tier}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {LANG_LABELS[lang] ?? lang}
          </span>
        </div>

        {summary && <p className="text-gray-600 mb-6">{summary}</p>}

        {episode?.vimeo_id ? (
          <div className="aspect-video mb-6 bg-black rounded-lg overflow-hidden shadow-lg">
            <iframe
              src={`https://player.vimeo.com/video/${episode.vimeo_id}?autoplay=0&title=0&byline=0&portrait=0`}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={chapter.title}
            />
          </div>
        ) : (
          <div className="aspect-video mb-6 bg-gray-200 rounded-lg flex items-center justify-center">
            <p className="text-gray-500">{vt.noVideo}</p>
          </div>
        )}

        <div className="flex items-center gap-4">
          <button
            onClick={handleMarkComplete}
            disabled={completing || completed}
            className={`px-6 py-3 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
              completed
                ? "bg-green-600 text-white cursor-default"
                : "bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            }`}
          >
            {completed ? vt.completed : completing ? vt.saving : vt.markComplete}
          </button>

          {completeError && (
            <p className="text-sm text-red-600">{completeError}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PeChapterViewer;
